import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import moment from 'moment';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from '../shared/services/notification.sevice';
import { Account, AccountService, PayerAttendedPaymentRequestTaskInput, PaymentRequestTaskService } from '../shared/_generated/backend';
import { determinePayeeAccountNumber, errorHandler } from '../helpers/form-helper';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

@Component({
    selector: 'app-init-payer-attended',
    templateUrl: './init-payer-attended.component.html',
    styleUrl: './init-payer-attended.component.scss',
    standalone: false
})
export class InitPayerAttendedComponent implements OnInit {

    accounts: Account[] = [];

    initPayerAttendedForm = this.fb.nonNullable.group({
        validityDate: ['', [Validators.required]],
        deadline: ['', [Validators.required]],
        payee: [null, [Validators.required]],
        payer: this.fb.nonNullable.group({
            accountNumber: [''],
            name: [''],
            emailAddress: ['', [Validators.required, Validators.email]]
        }),
        amount: [10, [Validators.required, Validators.min(1)]],
        amountModifiable: [false, [Validators.required]],
        comment: [''],
        commentModifiable: [true, [Validators.required]]
    });

    constructor(
        private fb: FormBuilder,
        private notificationService: NotificationService,
        private accountService: AccountService,
        private paymentRequestTaskService: PaymentRequestTaskService,
        public dialog: MatDialog
    ) { }

    async ngOnInit() {
        this.initPayerAttendedForm.controls.validityDate.setValue(moment().add(60, 'days').toISOString());
        this.initPayerAttendedForm.controls.deadline.setValue(moment().add(60, 'days').toISOString());

        this.accounts = await firstValueFrom(this.accountService.getAccounts());

        if (this.accounts.length === 1) {
            this.initPayerAttendedForm.controls.payee.setValue(this.accounts[0] as any);
        }

        this.setRequiredBasedOnModifiable(this.initPayerAttendedForm.controls.amountModifiable, this.initPayerAttendedForm.controls.amount);
    }

    openConfirmDialog() {
        const formValue = this.initPayerAttendedForm.getRawValue();

        const dialogRef: MatDialogRef<ConfirmDialogComponent, 'yes'> = this.dialog.open(
            ConfirmDialogComponent,
            { data: { type: 'payer-attended', paymentRequest: formValue } }
        );

        dialogRef.afterClosed().subscribe(result => {
            if (result === 'yes') {
                this.submit();
            }
        });
    }

    async submit() {
        try {
            const formValue = this.initPayerAttendedForm.getRawValue();

            const input: PayerAttendedPaymentRequestTaskInput = {
                ...formValue,
                validityDate: moment(formValue.validityDate).format('YYYY-MM-DD'),
                deadline: new Date(formValue.deadline),
                payee: {
                    accountNumber: determinePayeeAccountNumber(formValue)
                }
            };

            // fix default values of empty fields
            if (input.comment === null || input.comment === '') {
                delete input.comment;
            }
            if (input.amount === null || input.amount === 0) {
                delete input.amount;
            }
            if (input.payer?.accountNumber === null || input.payer?.accountNumber === '') {
                delete input.payer.accountNumber;
            }
            if (input.payer?.name === null || input.payer?.name === '') {
                delete input.payer.name;
            }

            const response = await firstValueFrom(this.paymentRequestTaskService.initPayerAttended(input));
            if (response.error) {
                this.notificationService.showErrorSnackBar(`Hiba: ${response.error}`);
            } else {
                this.notificationService.showSuccessSnackBar(`Sikeres létrehozás! Azonosító: ${response.paymentRequestTaskId}`);
            }

        } catch (error: any) {
            errorHandler(error, this.notificationService);
        }
    }

    private setRequiredBasedOnModifiable(modifiableControl: FormControl<boolean>, inputControl: FormControl) {
        modifiableControl.valueChanges.subscribe(modifiable => {
            if (modifiable) {
                inputControl.removeValidators(Validators.required);
            } else {
                inputControl.addValidators(Validators.required);
            }
            inputControl.updateValueAndValidity();
        });
    }

}
